import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { TEXT_REASON_LABELS } from '../utils/recipientBuckets';
import { inZone } from '../utils/companyDate';
import { useCompanyTimezone } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Message History, opened from the button beside Message Customers. Two tabs:
//   Email  GET /api/email/log: one line per bulk email (no per-recipient rows
//          exist for email).
//   Texts  GET /api/sms/batches: one line per bulk text batch with counts by
//          status, expandable to one row per customer (GET
//          /api/sms/batches/:id); and GET /api/sms/log?unbatched=true for the
//          texts that are not part of a batch (test texts, arrival alerts,
//          welcome texts, replies). Shown only when texting is on.
// A welcome email (services/customerWelcome.js) writes an email_log row with
// template_used 'Welcome', so it lists on the Email tab as a one-recipient
// send, labelled as the welcome message.
// Both tabs take the same From / To date filter (calendar days in the
// company's time zone), and show times in that zone.

const when = (iso, timezone) => (iso ? new Date(iso).toLocaleString('en-US', inZone(timezone, {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
})) : '');

const STATUS_LABELS = {
  pending: 'Waiting', sending: 'Sending', queued: 'Queued', sent: 'Sent', delivered: 'Delivered',
  undelivered: 'Undelivered', failed: 'Failed', skipped: 'Skipped', received: 'Received',
};
const KIND_LABELS = { bulk: 'Bulk', arrival: 'On my way', keyword_reply: 'Keyword reply', inbound: 'Reply from customer', test: 'Test text', welcome: 'Welcome', direct: 'Direct text', service_request: 'Service request', skip_notice: 'Skipped stop' };

// The customer's name in a meta line or a batch's recipient row: bold, in the
// primary blue.
const Name = ({ children }) => <strong style={{ color: 'var(--color-primary, #1e88e5)' }}>{children}</strong>;

// Who a text that is not part of a batch was with: "{name} replied" for an
// inbound text, the kind then "to {name}" for an outbound one, and a keyword
// reply keeps its label with the name beside it.
const singleWho = (m) => {
  const label = KIND_LABELS[m.kind] || m.kind;
  if (!m.customerName) return label;
  if (m.direction === 'inbound' || m.kind === 'inbound') return <><Name>{m.customerName}</Name> replied</>;
  if (m.kind === 'keyword_reply') return <>{label} &middot; <Name>{m.customerName}</Name></>;
  return <>{label} &middot; to <Name>{m.customerName}</Name></>;
};

const countsLine = (counts) => {
  const c = counts || {};
  const n = (...k) => k.reduce((a, s) => a + (c[s] || 0), 0);
  return `${n('delivered')} delivered, ${n('pending', 'sending', 'queued', 'sent')} queued, ${n('failed', 'undelivered')} failed, ${n('skipped')} skipped`;
};

function BatchRows({ batchId }) {
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  useEffect(() => {
    let live = true;
    axios.get(`${API_URL}/api/sms/batches/${batchId}`)
      .then((res) => { if (live) setState({ loading: false, data: res.data, error: '' }); })
      .catch((err) => { if (live) setState({ loading: false, data: null, error: err.response?.data?.error || 'Could not load this batch' }); });
    return () => { live = false; };
  }, [batchId]);
  if (state.loading) return <div className="text-muted" style={{ padding: '8px 0' }}>Loading...</div>;
  if (state.error) return <div className="alert alert-error">{state.error}</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--color-text-light)' }}>
            <th style={{ padding: '4px 6px' }}>Customer</th>
            <th style={{ padding: '4px 6px' }}>Status</th>
            <th style={{ padding: '4px 6px' }}>Segments</th>
            <th style={{ padding: '4px 6px' }}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {state.data.rows.map((r) => (
            <tr key={r.id} style={{ borderTop: '1px solid #f0f0f0' }}>
              <td style={{ padding: '4px 6px' }}>{r.customerName ? <Name>{r.customerName}</Name> : 'Removed customer'}</td>
              <td style={{ padding: '4px 6px' }}>{STATUS_LABELS[r.status] || r.status}</td>
              <td style={{ padding: '4px 6px' }}>{r.segments ?? ''}</td>
              <td style={{ padding: '4px 6px', color: 'var(--color-text-light)' }}>
                {r.status === 'skipped' ? (TEXT_REASON_LABELS[r.errorMessage] || r.errorMessage) : (r.errorCode ? `Error ${r.errorCode}` : (r.errorMessage || ''))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MessageHistory({ show, onClose, smsEnabled = false }) {
  const timezone = useCompanyTimezone();
  const [tab, setTab] = useState('email');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [email, setEmail] = useState({ loading: false, rows: [], error: '' });
  const [texts, setTexts] = useState({ loading: false, batches: [], singles: [], error: '' });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (show) { setTab('email'); setExpanded(null); }
  }, [show]);

  const params = useCallback(() => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    return p;
  }, [from, to]);

  useEffect(() => {
    if (!show || tab !== 'email') return undefined;
    let live = true;
    setEmail((s) => ({ ...s, loading: true, error: '' }));
    axios.get(`${API_URL}/api/email/log`, { params: params() })
      .then((res) => { if (live) setEmail({ loading: false, rows: res.data, error: '' }); })
      .catch((err) => { if (live) setEmail({ loading: false, rows: [], error: err.response?.data?.error || 'Could not load email history' }); });
    return () => { live = false; };
  }, [show, tab, params]);

  useEffect(() => {
    if (!show || tab !== 'texts') return undefined;
    let live = true;
    setTexts((s) => ({ ...s, loading: true, error: '' }));
    Promise.all([
      axios.get(`${API_URL}/api/sms/batches`, { params: params() }),
      axios.get(`${API_URL}/api/sms/log`, { params: { ...params(), unbatched: 'true', pageSize: 100 } }),
    ])
      .then(([b, l]) => { if (live) setTexts({ loading: false, batches: b.data.batches, singles: l.data.messages, error: '' }); })
      .catch((err) => { if (live) setTexts({ loading: false, batches: [], singles: [], error: err.response?.data?.error || 'Could not load text history' }); });
    return () => { live = false; };
  }, [show, tab, params]);

  if (!show) return null;

  const tabButton = (key, label) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      className={`btn btn-sm ${tab === key ? 'btn-primary' : 'btn-outline'}`}
      onClick={() => setTab(key)}
    >
      {label}
    </button>
  );
  const card = { border: '1px solid #e0e0e0', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '760px', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Message History">
        <div className="modal-header">
          <h2>Message History</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div style={{ padding: '16px 24px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end', marginBottom: '16px' }}>
            <div role="tablist" style={{ display: 'flex', gap: '8px', marginRight: 'auto' }}>
              {tabButton('email', 'Email')}
              {smsEnabled && tabButton('texts', 'Texts')}
            </div>
            <label style={{ fontSize: '13px' }}>
              From{' '}
              <input type="date" className="form-input" style={{ width: 'auto', display: 'inline-block' }} value={from} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label style={{ fontSize: '13px' }}>
              To{' '}
              <input type="date" className="form-input" style={{ width: 'auto', display: 'inline-block' }} value={to} onChange={(e) => setTo(e.target.value)} />
            </label>
            {(from || to) && <button type="button" className="btn btn-sm btn-outline" onClick={() => { setFrom(''); setTo(''); }}>Clear dates</button>}
          </div>

          {tab === 'email' && (
            <div role="tabpanel">
              {email.error && <div className="alert alert-error">{email.error}</div>}
              {email.loading && <div className="text-muted">Loading...</div>}
              {!email.loading && !email.error && email.rows.length === 0 && <div className="text-muted">No emails sent in this period.</div>}
              {!email.loading && email.rows.map((e) => (
                <div key={e.id} style={card}>
                  <div style={{ fontWeight: 600 }}>{e.subject}</div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>
                    {when(e.sent_at, timezone)} &middot; {e.recipient_count} recipient{e.recipient_count === 1 ? '' : 's'}
                    {e.sent_by_name ? ` · sent by ${e.sent_by_name}` : ''}
                    {e.template_used === 'Welcome' ? ' · welcome message' : e.template_used ? ` · template ${e.template_used}` : ''}
                  </div>
                </div>
              ))}
              {!email.loading && email.rows.length === 50 && <div className="text-muted" style={{ fontSize: '12px' }}>Showing the newest 50. Narrow the dates to see older sends.</div>}
            </div>
          )}

          {tab === 'texts' && (
            <div role="tabpanel">
              {texts.error && <div className="alert alert-error">{texts.error}</div>}
              {texts.loading && <div className="text-muted">Loading...</div>}
              {!texts.loading && !texts.error && texts.batches.length === 0 && texts.singles.length === 0 && (
                <div className="text-muted">No texts in this period.</div>
              )}
              {!texts.loading && texts.batches.length > 0 && <h3 style={{ fontSize: '1rem', margin: '0 0 8px' }}>Bulk texts</h3>}
              {!texts.loading && texts.batches.map((b) => (
                <div key={b.id} style={card}>
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === b.id ? null : b.id)}
                    aria-expanded={expanded === b.id}
                    style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}
                  >
                    <div style={{ whiteSpace: 'pre-wrap' }}>{b.body}</div>
                    <div style={{ fontSize: '13px', color: 'var(--color-text-light)', marginTop: '4px' }}>
                      {when(b.createdAt, timezone)} &middot; <Name>{b.recipientCount} recipient{b.recipientCount === 1 ? '' : 's'}</Name> &middot; {b.segments} segment{b.segments === 1 ? '' : 's'}
                      {b.channel === 'both' ? ' · with email' : ''}
                      {b.sentBy ? ` · sent by ${b.sentBy}` : ''}
                      {b.finishedAt ? '' : ' · still sending'}
                    </div>
                    <div style={{ fontSize: '13px', marginTop: '2px' }}>{countsLine(b.counts)} {expanded === b.id ? '▲' : '▼'}</div>
                  </button>
                  {expanded === b.id && <div style={{ marginTop: '8px' }}><BatchRows batchId={b.id} /></div>}
                </div>
              ))}
              {!texts.loading && texts.batches.length === 50 && <div className="text-muted" style={{ fontSize: '12px' }}>Showing the newest 50 batches. Narrow the dates to see older ones.</div>}

              {!texts.loading && texts.singles.length > 0 && <h3 style={{ fontSize: '1rem', margin: '16px 0 8px' }}>Other texts</h3>}
              {!texts.loading && texts.singles.map((m) => (
                <div key={m.id} style={card}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-light)', marginTop: '4px' }}>
                    {when(m.createdAt, timezone)} &middot; {singleWho(m)} &middot; {STATUS_LABELS[m.status] || m.status}
                    {m.errorCode ? ` · error ${m.errorCode}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default MessageHistory;
